// @flow
import * as React from 'react';
import idx from 'idx';
import { graphql } from 'react-apollo';
import { memoize, isEmpty, isEqual } from 'lodash';
import update from 'immutability-helper';

import GET_AUTO_MESSAGE_QUERY from 'admin/components/AdminAutoMessages/queries/GetAutoMessageByUuid.gql';
import AUTO_MESSAGE_SENDS_SUBSCRIPTION from 'admin/components/AdminAutoMessages/queries/AutoMessageSendsSubscription.gql';
import AUTO_MESSAGE_UNSENT_FILE_UPLOADS_SUBSCRIPTION from './queries/UnsentFileUploadsSubscription.gql';

import type {
  GetAutoMessageByUuid,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_previousSends_edges as GetAutoMessageByUuidEdge,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_previousSends_pageInfo as GetAutoMessageByUuidPageInfo,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageByUuid';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import Row from 'components/Row';

import type { SubscribeToMoreSendsByUuid as SubscribeToMoreSendsByUuidType } from 'admin/components/AdminAutoMessages/queries/__generated__/SubscribeToMoreSendsByUuid';
import type { SubscribeToUnsentFileUploads as SubscribeToUnsentFileUploadsType } from './queries/__generated__/SubscribeToUnsentFileUploads';

type Props = {
  children: (props: {
    renderLoading: ?React.Element,
    renderPaginatedLoading: ?React.Element,
    renderError: ?React.Element,
    autoMessage: ?GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages,
    loadNextPage: () => void,
  }) => React.Node,
  data: ReactApolloData<GetAutoMessageByUuid>,
  messageUuid: string,
};

function getAutoMessageFromData(data: ReactApolloData<GetAutoMessageByUuid>) {
  return idx(data, _ => _.autoMessaging.autoMessages.autoMessages[0]);
}

function updatePreviousSends(
  previousResult: GetAutoMessageByUuid,
  edges: GetAutoMessageByUuidEdge[],
  pageInfo: GetAutoMessageByUuidPageInfo
) {
  return update(previousResult, {
    autoMessaging: {
      autoMessages: {
        autoMessages: {
          '0': {
            previousSends: {
              pageInfo: {
                $set: pageInfo,
              },
              edges: { $set: edges },
            },
          },
        },
      },
    },
  });
}

class AdminAutoMessageDetailContainer extends React.Component<Props, void> {
  componentDidMount() {
    const autoMessage = getAutoMessageFromData(this.props.data);
    if (!autoMessage) {
      return;
    }

    if (autoMessage.status === 'active') {
      this.subscribeToMoreSends();
      return;
    }

    this.subscribeToUnsentFileUploads();
  }

  componentDidUpdate(prevProps: Props) {
    const prevAutoMessage = getAutoMessageFromData(prevProps.data);
    const autoMessage = getAutoMessageFromData(this.props.data);
    if (!autoMessage || isEqual(prevAutoMessage, autoMessage)) {
      return;
    }

    if (autoMessage.status === 'active') {
      this.subscribeToMoreSends();
    } else {
      this.subscribeToUnsentFileUploads();
    }
  }

  loadMoreSends = memoize<[string, string], void>((messageUuid: string, cursor: string) => {
    this.props.data.fetchMore({
      variables: {
        uuid: messageUuid,
        cursor,
        limit: 20,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const existingAutoMessage = idx(
          previousResult,
          _ => _.autoMessaging.autoMessages.autoMessages[0]
        );
        const newAutoMessage = idx(
          fetchMoreResult,
          _ => _.autoMessaging.autoMessages.autoMessages[0]
        );
        if (!existingAutoMessage || !newAutoMessage) {
          return previousResult;
        }

        const existingSends = idx(existingAutoMessage, _ => _.previousSends.edges) || [];
        const newSends = newAutoMessage.previousSends.edges.slice(1);
        if (isEmpty(newSends)) {
          return previousResult;
        }

        return updatePreviousSends(
          previousResult,
          [...existingSends, ...newSends],
          newAutoMessage.previousSends.pageInfo
        );
      },
    });
  });

  loadNextPage = () => {
    const { data, messageUuid } = this.props;
    const hasNextPage = idx(
      data,
      _ => _.autoMessaging.autoMessages.autoMessages[0].previousSends.pageInfo.hasNextPage
    );
    const existingSends = idx(
      data,
      _ => _.autoMessaging.autoMessages.autoMessages[0].previousSends.edges
    );
    const cursor = existingSends && existingSends.length > 0 && existingSends.slice(-1)[0].cursor;
    if (hasNextPage && cursor) {
      this.loadMoreSends(messageUuid, cursor);
    }
  };

  subscribeToMoreSends = () => {
    const { data, messageUuid } = this.props;
    data.subscribeToMore({
      document: AUTO_MESSAGE_SENDS_SUBSCRIPTION,
      variables: { messageUuid },
      updateQuery: (
        prev,
        { subscriptionData }: { subscriptionData: { data: SubscribeToMoreSendsByUuidType } }
      ) => {
        const prevMessage = idx(prev, _ => _.autoMessaging.autoMessages.autoMessages[0]);
        if (!subscriptionData || !prevMessage) {
          return prev;
        }

        // if there isn't any data in the subscription payload, or if the send uuid
        // already matches a send that's in the apollo cache, ignore it and don't
        // update the cache
        const newSendNode = idx(subscriptionData, _ => _.data.watchAutoMessageSends);
        const prevSends = idx(prevMessage, _ => _.previousSends.edges) || [];
        if (!newSendNode || prevSends.some(send => send.node.uuid === newSendNode.uuid)) {
          return prev;
        }

        // otherwise, push the new send into the apollo cache
        const newSend = { __typename: 'AutoMessageSendEdge', node: newSendNode, cursor: '' };
        return updatePreviousSends(
          prev,
          [newSend, ...prevSends],
          prevMessage.previousSends.pageInfo
        );
      },
    });
  };

  subscribeToUnsentFileUploads = () => {
    const { data, messageUuid } = this.props;
    data.subscribeToMore({
      document: AUTO_MESSAGE_UNSENT_FILE_UPLOADS_SUBSCRIPTION,
      variables: { messageUuid },
      updateQuery: (
        prev,
        { subscriptionData }: { subscriptionData: { data: SubscribeToUnsentFileUploadsType } }
      ) => {
        const prevMessage = idx(prev, _ => _.autoMessaging.autoMessages.autoMessages[0]);
        if (!subscriptionData || !prevMessage) {
          return prev;
        }

        const newFileUpload = idx(subscriptionData, _ => _.data.watchAutoMessageFileUploads);
        const prevFileUploads = idx(prevMessage, _ => _.unsentFileUploads) || [];

        // if there isn't any data in the subscription payload, or if the file upload
        // already matches a file upload that's in the apollo cache, ignore it and don't
        // update the cache
        if (!newFileUpload || prevFileUploads.some(upload => upload.uuid === newFileUpload.uuid)) {
          return prev;
        }

        // otherwise, push the new file upload into the apollo cache
        return update(prev, {
          autoMessaging: {
            autoMessages: {
              autoMessages: {
                '0': {
                  unsentFileUploads: { $set: [newFileUpload, ...prevFileUploads] },
                },
              },
            },
          },
        });
      },
    });
  };

  render() {
    const { data, children } = this.props;
    const autoMessage = getAutoMessageFromData(data);
    const renderLoading = data.loading && !autoMessage ? <CenteredLoadingIndicator /> : null;
    const renderPaginatedLoading =
      data.loading && autoMessage ? (
        <Row>
          <CenteredLoadingIndicator size="small" />
        </Row>
      ) : null;
    const renderError =
      !data.loading && !autoMessage ? (
        <ErrorReporter error="AdminAutoMessageDetailView tried to render without an autoMessage" />
      ) : null;

    return children({
      renderLoading,
      renderPaginatedLoading,
      renderError,
      autoMessage,
      loadNextPage: this.loadNextPage,
    });
  }
}

const withAutoMessages = graphql(GET_AUTO_MESSAGE_QUERY, {
  options: ({ messageUuid }) => ({
    variables: { messageUuid },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  }),
});

export { AdminAutoMessageDetailContainer };

export default withAutoMessages(AdminAutoMessageDetailContainer);
